import styled from 'styled-components';

import {
  CheckMetadata,
  createSimpleCheckMetadataArray,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import RiskLevelSticker from 'dashboard/components/RiskLevelSticker';
import { IdPaperDetection } from 'dashboard/models/check';
import { RiskData } from 'dashboard/models/verification-check';

const UnorderedList = styled('ul')`
  list-style-type: none;
`;

const ListItem = styled('li')`
  margin: 0;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

const adverseScenarioText: Map<string, string> = new Map([
  [
    'photocopyDetected',
    'Image may be a photocopy. Distinguishing between original printed IDs and photocopies is challenging due to similarities in texture, color, and details.',
  ],
  [
    'lowerConfidenceRegion',
    'Image may be a genuine printed ID. In countries where IDs are officially printed on paper, differentiating between authentic and reproduced versions becomes more challenging.',
  ],
]);

export const computeIdPaperDetectionMetadata = (check: IdPaperDetection): CheckMetadata => {
  const {
    'photocopy-detected': photocopyDetected,
    'lower-confidence-region': lowerConfidenceRegion,
    risks: risks,
  } = check.metadata ?? {};

  const metadata = [];

  const paperDetectionRiskData = risks?.find(
    (r: RiskData) => (r.reason as string) === 'paper_id_detected',
  );

  if (paperDetectionRiskData) {
    metadata.push({
      label: 'Paper Detection Risk',
      value: RiskLevelSticker({ riskLevel: paperDetectionRiskData['level']! }),
    });
  }

  if (photocopyDetected || lowerConfidenceRegion) {
    const adverseScenarios = [];
    if (photocopyDetected) {
      adverseScenarios.push('photocopyDetected');
    }
    if (lowerConfidenceRegion) {
      adverseScenarios.push('lowerConfidenceRegion');
    }

    metadata.push({
      label: 'Adverse Scenarios',
      value: (
        <UnorderedList>
          {adverseScenarios.map((adverseScenario) => (
            <ListItem key={adverseScenario}>{adverseScenarioText.get(adverseScenario)}</ListItem>
          ))}
        </UnorderedList>
      ),
      labelHelpText:
        "Adverse scenarios are unexpected or challenging conditions that can affect a model's accuracy. These conditions might lead to decreased performance or erroneous predictions.",
    });
  }

  return {
    metadata: createSimpleCheckMetadataArray(metadata),
    hideIfVerificationPassed: false,
  };
};
